import {
  API_BASE,
  type ChatChannelMemberResponse,
  type ChatChannelResponse,
  type ChatMessageResponse,
  type ChatMessageSearchHitResponse,
} from '@/lib/api';
import { channelDisplayName } from '@/lib/chat-display';
import {
  CHAT_FILE_TYPE_POLICY,
  buildFileAcceptAttribute,
  isMimeInlineSafe,
  resolveFileType,
} from '@fias/platform-resources';

/**
 * Filters channel members for the @-mention typeahead: a case-insensitive
 * substring match over display name / username / nickname (Slack/Discord-style —
 * you can type either the handle or the shown name), excluding the current user.
 * An empty/whitespace query returns everyone (minus self). Pure so the composer's
 * static-filter path is unit-testable independent of the editor.
 */
export function filterChannelMembersByQuery(
  members: ChatChannelMemberResponse[],
  query: string,
  excludeUserId: string | null,
): ChatChannelMemberResponse[] {
  const q = query.trim().toLowerCase();
  return members.filter((m) => {
    if (m.userId === excludeUserId) return false;
    if (!q) return true;
    return (
      (m.displayName || m.userId).toLowerCase().includes(q) ||
      (m.username?.toLowerCase().includes(q) ?? false) ||
      (m.nickname?.toLowerCase().includes(q) ?? false)
    );
  });
}

/**
 * Filters the loaded conversation list for the sidebar filter box: a
 * case-insensitive substring match over the conversation's shown name (a DM
 * partner's display name, a channel's name, "Saved Messages"), a DM partner's
 * handle, and the owning guild's name — so typing a guild shows its channels.
 * An empty/whitespace query returns every conversation unchanged. Pure, like
 * {@link filterChannelMembersByQuery}, so the list's filtering is unit-testable.
 *
 * Only ever sees what the client has loaded (the first page of the channel
 * list); the global search panel is where an older conversation is found.
 */
export function filterChannelsByQuery(
  channels: ChatChannelResponse[],
  guilds: ReadonlyArray<{ guildId: string; name: string }>,
  query: string,
): ChatChannelResponse[] {
  const q = query.trim().toLowerCase();
  if (!q) return channels;
  // Stored handles carry no "@"; a typed "@wjones" is the same handle as
  // "wjones" (the people search strips the sigil the same way).
  const handle = q.replace(/^@/, '');
  const guildNameById = new Map(guilds.map((g) => [g.guildId, g.name.toLowerCase()]));
  return channels.filter(
    (c) =>
      channelDisplayName(c).toLowerCase().includes(q) ||
      (handle.length > 0 && (c.dmPartnerUsername?.toLowerCase().includes(handle) ?? false)) ||
      (c.guildId ? (guildNameById.get(c.guildId)?.includes(q) ?? false) : false),
  );
}

// Channel labelling lives in lib/ so arches integrating with chat via its
// public API (e.g. customer-support's share-to-chat) can label channels without
// importing from this arche's directory. Re-exported here so chat-internal
// imports keep their natural path.
export { SELF_DM_LABEL, channelDisplayName } from '@/lib/chat-display';

/**
 * Conversation label for an all-chats search hit. Prefers the loaded channel
 * (so DMs read as the partner's name, self-DMs as "Saved Messages"), with named
 * channels prefixed "#" — the same convention as the in-channel search
 * placeholder. Falls back to what the hit itself carries when the channel isn't
 * in the loaded list.
 */
export function searchHitChannelLabel(
  hit: Pick<ChatMessageSearchHitResponse, 'channelType' | 'channelName'>,
  channel: ChatChannelResponse | undefined,
): string {
  if (channel) {
    const name = channelDisplayName(channel);
    return channel.channelType === 'direct' || channel.isSelfDm === true ? name : `#${name}`;
  }
  if (hit.channelType === 'direct') return 'Direct message';
  return hit.channelName ? `#${hit.channelName}` : 'Channel';
}

// The file-type policy is shared platform code (`@fias/platform-resources`) and
// surface-neutral. Chat binds it to `CHAT_FILE_TYPE_POLICY` here so chat-internal
// callers don't repeat the policy argument at every call site.
export { CHAT_MESSAGE_MAX_ATTACHMENTS } from '@/lib/api';

/** Resolve a picked file to the canonical type chat will store, or a rejection. */
export function resolveChatAttachmentType(fileName: string, declaredMimeType: string) {
  return resolveFileType(fileName, declaredMimeType, CHAT_FILE_TYPE_POLICY);
}

/** Whether a STORED chat attachment mime may ever be rendered in-page. */
export function isChatAttachmentMimeInlineSafe(mimeType: string): boolean {
  return isMimeInlineSafe(mimeType, CHAT_FILE_TYPE_POLICY);
}

/**
 * `accept` for the composer's Document picker. Computed once — it is a pure
 * function of a frozen catalog, and it is read during render.
 *
 * Narrower than what chat actually accepts (the policy stores unrecognized
 * types opaquely), so drag-and-drop and the Vault picker stay open to formats
 * this string doesn't name. It exists to give the OS dialog a sensible default
 * filter, not to gate anything.
 */
export const CHAT_ATTACHMENT_DOCUMENT_ACCEPT = buildFileAcceptAttribute(CHAT_FILE_TYPE_POLICY, {
  excludeMedia: true,
});

/** Per-type chat send permissions, as resolved for the current member. */
export interface ChatAttachmentTypePermissions {
  canSendImages: boolean;
  canSendVideos: boolean;
  canSendAudio: boolean;
  canSendFiles: boolean;
}

/**
 * Whether the current member may attach a file of this MIME type, mirroring the
 * server's `mimeTypeToPermissionKey` exactly (image→images, video→videos,
 * audio→audio, everything else→files). Single source of truth so the composer's
 * drag-and-drop filter and the Vault picker never drift from each other or from
 * the server's `assertCanSendChatAttachment` gate.
 */
export function isChatAttachmentTypeAllowed(
  mimeType: string,
  perms: ChatAttachmentTypePermissions,
): boolean {
  const mainType = (mimeType || '').split('/')[0].toLowerCase();
  if (mainType === 'image') return perms.canSendImages;
  if (mainType === 'video') return perms.canSendVideos;
  if (mainType === 'audio') return perms.canSendAudio;
  return perms.canSendFiles;
}

/**
 * URL for a chat user's avatar image, served and presigned by the API and
 * keyed purely on userId. This lets the message list render any sender's
 * avatar — current user, other members, senders beyond the member-list page,
 * and WebSocket-delivered messages — without a members-list or payload
 * dependency. The endpoint 404s when the user has no avatar set; the caller
 * falls back to an initial.
 */
export function chatAvatarUrl(userId: string): string {
  return `${API_BASE}/arches/chat/users/${encodeURIComponent(userId)}/avatar`;
}

/**
 * Converts chat-message HTML (produced by the TipTap composer) into a single
 * line of plain text, suitable for compact previews — reply banners and quoted
 * snippets. Block boundaries (paragraphs, list items, line breaks) collapse to
 * spaces so words from adjacent blocks don't run together, and mention chips
 * reduce to their visible text.
 *
 * Previews must never render the raw HTML: doing so leaks literal tags (e.g.
 * "<p>") into the UI and lets long unbroken markup blow the layout past the
 * viewport edge.
 */
export function chatHtmlToPlainText(html: string): string {
  if (!html) return '';
  const withSpaces = html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|ul|ol|blockquote|pre)>/gi, ' ');
  const tmpl = document.createElement('template');
  tmpl.innerHTML = withSpaces;
  return (tmpl.content.textContent || '').replace(/\s+/g, ' ').trim();
}

/**
 * Converts chat-message HTML into plain text for the clipboard. Unlike
 * {@link chatHtmlToPlainText} (which flattens to one line for previews), this
 * preserves the message's line structure — block boundaries (paragraphs, list
 * items, <br>) become newlines — while trimming leading/trailing whitespace so
 * a copied message never carries stray blank lines ("paragraph marks") at the
 * start or end, and never leaks raw tags like "<p>".
 */
export function chatHtmlToCopyText(html: string): string {
  if (!html) return '';
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|ul|ol|blockquote|pre)>/gi, '\n');
  const tmpl = document.createElement('template');
  tmpl.innerHTML = withBreaks;
  const text = tmpl.content.textContent || '';
  return text
    .replace(/[ \t]+/g, ' ') // collapse horizontal whitespace runs
    .replace(/[ \t]*\n[ \t]*/g, '\n') // trim spaces hugging newlines
    .replace(/\n{3,}/g, '\n\n') // cap consecutive blank lines
    .trim();
}

/**
 * Derives a consistent two-stop gradient from a string seed.
 * Uses a djb2-style hash so the same name always produces the same colors.
 */
export function seededGradient(seed: string): string {
  let hash = 5381;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) + hash) ^ seed.charCodeAt(i);
  }
  hash = hash >>> 0;
  const h1 = hash % 360;
  const h2 = (h1 + 50 + ((hash >> 12) % 60)) % 360;
  return `linear-gradient(135deg, hsl(${h1}, 65%, 50%), hsl(${h2}, 65%, 40%))`;
}

// ---------------------------------------------------------------------------
// Clipboard attachments
// ---------------------------------------------------------------------------

/**
 * The only MIME types eligible for renaming, mapped to the extension we give
 * them. Deliberately a closed list rather than a guess derived from the MIME:
 * the extension we write is the one `resolveFileType` will judge the file by, so
 * inventing one is a way to launder a file past the policy (see below).
 *
 * Confined to the forms browsers actually synthesize for clipboard bitmap data.
 * `image/heic` and `image/svg+xml` are deliberately absent — a HEIC is a camera
 * file off a disk and an SVG is not a bitmap at all, so a paste carrying either
 * is a real file whose name we must not touch.
 */
const CLIPBOARD_IMAGE_EXTENSIONS: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
  'image/tiff': 'tiff',
};

/** Base names browsers synthesize for bitmap clipboard data — no real file behind them. */
const SYNTHESIZED_CLIPBOARD_BASE_RE = /^(image|screenshot|untitled)$/i;

/**
 * Whether this file is browser-synthesized bitmap data we may safely rename.
 *
 * **Renaming must never drop an extension.** `resolveFileType` checks
 * `blockedExtensions` FIRST, so replacing `image.exe` with a name built from the
 * declared MIME strips the very thing the executable blocklist matches on — and
 * because the server re-resolves from the filename the client sends, that
 * launders the file past both gates (`image.exe` + `application/octet-stream`
 * went from `blocked-type` to accepted-and-stored). So we rename only when there
 * is nothing to strip: a recognized image MIME, and a name that is either absent
 * or the synthesized base with at most the extension that MIME already implies.
 *
 * **Accepted trade-off:** nothing in `clipboardData` distinguishes a synthesized
 * bitmap from a real file — `DataTransferItem.kind` is `'file'` for both, and so
 * is `types`. So a genuine disk file named exactly `image.png` / `screenshot.jpg`
 * / `untitled.gif` is renamed too. That is the deliberate choice: those three
 * basenames carry no information a timestamped name does not carry better, and
 * the alternatives are worse. Renaming only unnamed files would rename nothing
 * at all (browsers always supply `image.png`), and keying off a fresh
 * `lastModified` fails in the direction that reintroduces duplicate names, which
 * the upload tracker uses to key progress and cancellation.
 */
function synthesizedImageExtension(file: File): string | null {
  const mime = (file.type || '').split(';')[0].trim().toLowerCase();
  const ext = CLIPBOARD_IMAGE_EXTENSIONS[mime];
  if (!ext) return null;
  const name = file.name.trim().toLowerCase();
  if (name.length === 0) return ext;
  const base = name.endsWith(`.${ext}`) ? name.slice(0, -(ext.length + 1)) : name;
  return SYNTHESIZED_CLIPBOARD_BASE_RE.test(base) ? ext : null;
}

// Names must be unique: the upload tracker keys per-file progress and
// cancellation by filename, so two attachments sharing a name would share one
// progress chip and one cancel. The timestamp alone is not enough — a double
// Ctrl+V lands twice in the same second — so we remember how many names we have
// already issued for the current second and disambiguate in the OS style.
let lastPastedStamp = '';
let namesIssuedForStamp = 0;

/**
 * Name for a pasted bitmap, in the OS screenshot style ("Pasted image 2026-08-27
 * at 14-32-05.png", then " (2)", " (3)" within the same second).
 */
function pastedImageFileName(extension: string, now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp =
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    ` at ${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
  if (stamp === lastPastedStamp) {
    namesIssuedForStamp += 1;
  } else {
    lastPastedStamp = stamp;
    namesIssuedForStamp = 0;
  }
  const suffix = namesIssuedForStamp > 0 ? ` (${namesIssuedForStamp + 1})` : '';
  return `Pasted image ${stamp}${suffix}.${extension}`;
}

export function clipboardAttachmentFiles(
  clipboardData: DataTransfer | null,
  now: Date = new Date(),
): File[] {
  if (!clipboardData) return [];
  if (clipboardData.getData('text/plain').trim().length > 0) return [];
  const files = Array.from(clipboardData.files ?? []);
  if (files.length === 0) return [];
  return files.map((file) => {
    const extension = synthesizedImageExtension(file);
    if (!extension) return file;
    const renamed = pastedImageFileName(extension, now);
    return new File([file], renamed, { type: file.type, lastModified: file.lastModified });
  });
}

/** Longest app name the "via" label shows before truncating (it shares a line with the time). */
const POSTED_VIA_NAME_MAX = 32;

/**
 * "via <app>" for a message an app posted as its author — the server-written
 * `metadata.postedVia` (a plugin's guild-chat post) — or null for a message
 * its author typed. A message whose app name could not be read still says an
 * app posted it.
 */
export function postedViaLabel(metadata: ChatMessageResponse['metadata']): string | null {
  const via = metadata?.postedVia;
  if (!via || via.kind !== 'arche') return null;
  const name = typeof via.archeName === 'string' ? via.archeName.trim() : '';
  if (!name) return 'via an app';
  return name.length > POSTED_VIA_NAME_MAX
    ? `via ${name.slice(0, POSTED_VIA_NAME_MAX - 1)}…`
    : `via ${name}`;
}
